import { notEmpty, type Optional } from '@festina-life/core'
import { useEffect, useEffectEvent } from 'react'

import { MAX_TAB_LIMIT } from '../../context/defaultPreferences'
import { useLayoutContext } from '../../context/layoutContext'
import { isSameTabTarget } from './isSameTabTarget'
import type { TabLink } from './types'

type Props = {
  visibleTabLinks: Optional<TabLink[]>
  updateTabLinks: (nextValue: TabLink[]) => void
}

export const useEnsureTabLimits = ({
  visibleTabLinks,
  updateTabLinks,
}: Props) => {
  const {
    preferences: { tabs },
  } = useLayoutContext()
  const currentLimit = notEmpty(tabs.limit) ? tabs.limit : MAX_TAB_LIMIT
  // The limit must only be enforced when the tab links or the limit change. Reacting
  // to a new `updateTabLinks` would evict again while `visibleTabLinks` is still deferred.
  const onLimitExceeded = useEffectEvent(updateTabLinks)

  useEffect(() => {
    if (!visibleTabLinks || visibleTabLinks?.length <= currentLimit) {
      return
    }

    const sortedTabLinks = [...visibleTabLinks].sort(
      (tabA, tabB) =>
        (tabB.lastVisitedTimestamp ?? 0) - (tabA.lastVisitedTimestamp ?? 0),
    )

    const remainingTabLinks = sortedTabLinks.slice(0, currentLimit)

    onLimitExceeded(
      visibleTabLinks.filter((link) =>
        remainingTabLinks.some((remainingLink) =>
          isSameTabTarget(remainingLink, link),
        ),
      ),
    )
  }, [currentLimit, visibleTabLinks])
}
